/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/alt-text */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import SearchIcon from '@mui/icons-material/Search';
// import LockIcon from '@mui/icons-material/Lock';
import FolderIcon from '@mui/icons-material/Folder';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ProgramCard from './ProgramCard';
import ProgramDrawer from './ProgramDrawer';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import LoadingContent from '../../components/LoadingContent';
import SelectInput from '../../components/input/SelectInput';
import { useFormik } from 'formik';
import wpGeodirServices from "../../services/wpGeodirServices";
import { useHistory, useLocation } from 'react-router-dom';
import { appRoutes, routers } from '../../router/router';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { logout } from '../../app/basicInformationSlice/basicInformationSlice';
import { getAccessToken, getUserId, removeAccessToken, removeUserId } from '../../utils/sessionStorage';
import { STRIPE_PRICE_NAME } from '../../constants/common';
import toast from 'react-hot-toast';

/** Map legacy URL/bookmark values (numeric id) to category name for API params. */
function resolveCategoryForApi(raw: string, categoriesList: any[]): string {
  if (!raw || !categoriesList?.length) return raw;
  const trimmed = String(raw).trim();
  const byId = categoriesList.find((c) => String(c.id) === trimmed);
  if (byId?.name != null) return String(byId.name);
  return trimmed;
}

function buildSearchValuesFromQuery(search: string, categoryList: any[]) {
  const q = new URLSearchParams(search);
  const rawCategory = q.get('category') || '';
  return {
    title: q.get('name') || '',
    category: resolveCategoryForApi(rawCategory, categoryList),
    offer_in: q.get('offer_in') || '',
    grades: q.get('grades') || '',
    format: q.get('format') || '',
    location: q.get('location') || '',
    delivery: q.get('delivery') || '',
    cost_range: q.get('cost_range') || '',
    duration: q.get('duration') || '',
    min_cost: q.get('min_cost') || '0',
    max_cost: q.get('max_cost') || '10000',
    application_deadline: q.get('application_deadline') || '',
  };
}

const categoryCountTagSx: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#6b7280',
  backgroundColor: '#f3f4f6',
  padding: '2px 8px',
  borderRadius: 9999,
  flexShrink: 0,
};

/** Show counts above 2000 as "2000+" in category filter tags. */
function formatCategoryProgramCountDisplay(count: number | null | undefined): string | null {
  if (count == null || Number.isNaN(Number(count))) return null;
  const n = Number(count);
  if (n > 2000) return '2000+';
  return String(n);
}

function renderCategoryOptionWithCount(opt: {
  value: string;
  name: string;
  programCount?: number | null;
}) {
  const countLabel = formatCategoryProgramCountDisplay(opt.programCount);
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: 8,
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.name}</span>
      {countLabel != null && <span style={categoryCountTagSx}>{countLabel}</span>}
    </span>
  );
}

function selectedCategoryDisplay(
  selected: any,
  categoryOptions: { value: string; name: string; programCount?: number | null }[],
) {
  const raw =
    selected && typeof selected === 'object' && selected !== null && 'value' in selected
      ? (selected as { value: string }).value
      : selected;
  const opt = categoryOptions.find((o) => o.value === raw);
  if (opt) {
    // Closed select: show label only (counts stay in the dropdown list).
    return opt.name ?? '';
  }
  if (typeof raw === 'string' && raw.length > 0) {
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }
  return raw ?? '';
}

interface Props {}

const ProgramsPage = (props: Props) => {
  const [loading, setLoading] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState<boolean>(window.innerWidth <= 768);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const [categories, setCategories] = React.useState<any[]>([]);
  const [categoryOptions, setCategoryOptions] = React.useState<any[]>([]);
  const [programs, setPrograms] = React.useState<any[]>([]);
  const [count, setCount] = useState<any>(0);
  const [savedPrograms, setSavedPrograms] = React.useState<Set<number>>(new Set());
  const [savingPrograms, setSavingPrograms] = React.useState<Set<number>>(new Set());
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedProgram, setSelectedProgram] = React.useState<any>(null);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [limit] = React.useState<number>(9);
  const stripeSubscriptionInfo = useAppSelector((store) => store.basicInformation.stripeSubscriptionInfo);
  const isTrial = !stripeSubscriptionInfo || stripeSubscriptionInfo?.subcription_plan === STRIPE_PRICE_NAME.GradMapStarter;
  // const isTrial = false;

  // Determine page mode based on pathname
  // const isSummerProgramsPage = location.pathname === '/summerprograms' || location.pathname === '/summer-programs' || location.pathname.startsWith('/summer-programs');
  // const showCategories = isSummerProgramsPage ? true : false; // Show categories by default on summer programs page

  let query = new URLSearchParams(useLocation().search);  
  let search_title = query.get('name') || '';
  let search_category = query.get('category') || '';
  let search_offer_in = query.get('offer_in') || '';
  let search_grades = query.get('grades') || '';
  let search_format = query.get('format') || '';
  let search_location = query.get('location') || '';
  let search_delivery = query.get('delivery') || '';
  let search_cost_range = query.get('cost_range') || '';
  let search_duration = query.get('duration') || '';
  let search_min_cost = query.get('min_cost') || '';
  let search_max_cost = query.get('max_cost') || '';
  let search_application_deadline = query.get('application_deadline') || '';
  let studentId = getUserId();
  let token = getAccessToken();
  
  // Dynamic page title based on mode

  const formik = useFormik({
    initialValues: {
      title: search_title,
      category: search_category,
      offer_in: search_offer_in,
      grades: search_grades,
      format: search_format,
      location: search_location,
      delivery: search_delivery,
      cost_range: search_cost_range,
      duration: search_duration,
      min_cost: search_min_cost || '0',
      max_cost: search_max_cost || '10000',
      application_deadline: search_application_deadline,
    },
    onSubmit: async (values) => {
      studentId = getUserId();
      token = getAccessToken();
      if (token && studentId) {
        await handleSearch();
        showOptions();
      } 
      else {
        handleLogout();
      }
    },
  });

  const handleLogout = () => {
    dispatch(logout());
    removeAccessToken();
    removeUserId();
    history.push(routers.login.path);
  };

  const initSearch = () => {
    search_title = query.get('name') || '';
    search_category = query.get('category') || '';
    search_offer_in = query.get('offer_in') || '';
    search_grades = query.get('grades') || '';
    search_format = query.get('format') || '';
    search_location = query.get('location') || '';
    search_delivery = query.get('delivery') || '';
    search_cost_range = query.get('cost_range') || '';
    search_duration = query.get('duration') || '';
    search_min_cost = query.get('min_cost') || '';
    search_max_cost = query.get('max_cost') || '';
    search_application_deadline = query.get('application_deadline') || '';
    
    formik.resetForm({values: {
      title: search_title,
      category: search_category,
      offer_in: search_offer_in,
      grades: search_grades,
      format: search_format,
      location: search_location,
      delivery: search_delivery,
      cost_range: search_cost_range,
      duration: search_duration,
      min_cost: search_min_cost || '0',
      max_cost: search_max_cost || '10000',
      application_deadline: search_application_deadline,
    }});

    showOptions();
  }

  const SAVED_PROGRAMS_LIMIT_STARTER = 6;

  const toggleSaveProgram = async (programId: number, apiCalled: boolean = false) => {
    const isCurrentlySaved = savedPrograms.has(programId);
    
    // Starter users: can only save up to 6 programs
    if (isTrial && !isCurrentlySaved && savedPrograms.size >= SAVED_PROGRAMS_LIMIT_STARTER) {
      toast.error('You can save up to 6 programs on the Starter plan. Upgrade to save more.');
      return;
    }

    // Prevent multiple simultaneous requests for the same program
    if (savingPrograms.has(programId)) {
      return;
    }

    setSavingPrograms(prev => new Set(prev).add(programId));

    try {
      if (isCurrentlySaved) {
        // Unsave program
        if (!apiCalled) {
          // Only call API if it wasn't already called by ProgramCard
          await wpGeodirServices.unsaveProgram(programId);
          toast.success('Program removed from saved list');
        }
        // Update state immediately after successful API call (or if API was already called)
        // This ensures the card re-renders with the updated state
        setSavedPrograms(prev => {
          const newSet = new Set(prev);
          newSet.delete(programId);
          return newSet;
        });
      } else {
        // Save program
        // const response = await wpGeodirServices.saveProgram(programId);
        
        // Update state after successful API call
        // If API call succeeds (no error thrown), update the state
        setSavedPrograms(prev => {
          const newSet = new Set(prev);
          newSet.add(programId);
          return newSet;
        });
        // Show success notification
        toast.success('Program saved successfully');
      }
    } catch (error: any) {
      console.error('Error toggling save program:', error);
      // Show error message to user
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save/unsave program';
      toast.error(errorMessage);
      // Don't update state on error
    } finally {
      // Always remove from savingPrograms in finally block
      setSavingPrograms(prev => {
        const newSet = new Set(prev);
        newSet.delete(programId);
        return newSet;
      });
    }
  }

  // const onClickDetail = (item: any) => {
  //   let search_query = `id=${item.id}`;
  //   history.push({
  //     pathname: appRoutes.summerProgramsDetail.path,
  //     search: search_query,
  //   });
  // };

  const openDrawer = (item: any) => {
    setSelectedProgram(item);
    setDrawerOpen(true);
  };

  const renderPrograms = useMemo(() => {
    const itemsPerRow = 3;
    // All users (including Starter) see full results for current page
    const displayPrograms = programs;
    const rows: JSX.Element[] = [];    
    for (let i = 0; i < displayPrograms.length; i += itemsPerRow) {
      const columns: JSX.Element[] = [];

      for (let j = 0; j < itemsPerRow; j++) {
        const currentIndex = i + j;
        if (currentIndex < displayPrograms.length) {
          const item = displayPrograms[currentIndex];
          // New API returns `primary_category` as string; keep fallback for old shape.
          let category_name = item?.primary_category || '';
          if (!category_name) {
            let default_category = item?.default_category || '';
            let categoryObj = categories.find((o:any)=> o.id === Number(default_category));
            category_name = categoryObj ? categoryObj.name || '' : '';
          }
          // Check if program is saved from API response or from state
          let isSaved = item?.isSaved === true || savedPrograms.has(item.id);
          let isSaving = savingPrograms.has(item.id);
          const disableSave = isTrial && savedPrograms.size >= SAVED_PROGRAMS_LIMIT_STARTER && !isSaved;
          
          columns.push(
            <ProgramCard
              key={`program_${item.id}`}
              item={item}
              categoryName={category_name}
              isSaved={isSaved}
              isSaving={isSaving}
              onSaveToggle={toggleSaveProgram}
              onViewDetail={openDrawer}
              isTrial={isTrial}
              addManager={item?.addManager === true}
              disableSave={disableSave}
            />
          );
        }
      }

      rows.push(
        <div key={i} className="row" style={{marginBottom: '32px', marginLeft: '-16px', marginRight: '-16px', gap: '0'}}>
          {columns}
        </div>          
      );
    }    
    return rows;
  }, [programs, savedPrograms, savingPrograms, categories, isTrial]);

  const getData = async (updatedValues?: any, page: number = 1, categoriesList?: any[]) => {
    try {
      setLoading(true);
      
      // Use updatedValues if provided, otherwise fall back to formik.values
      const values = updatedValues || formik.values;
      const catList = categoriesList ?? categories;
      const categoryForApi = resolveCategoryForApi(values.category || '', catList);
      const offset = (page - 1) * limit;
      const body = {
        searchTerm: values.title,
        category: categoryForApi,
        offer_in: values.offer_in,
        grades: values.grades,
        format: values.format,
        location: values.location,
        delivery: values.delivery,
        cost_range: values.cost_range,
        duration: values.duration,
        min_cost: values.min_cost,
        max_cost: values.max_cost,
        application_deadline: values.application_deadline,
        offset: offset,
        limit: limit
      };
      let ret:any = await wpGeodirServices.getProgramListV2(body);
      if (ret) {
        if (ret.data) {
          // Replace programs with new page results
          setPrograms(ret.data);
          
          // Sync saved programs from API response
          const savedIds = new Set<number>();
          ret.data.forEach((program: any) => {
            if (program.isSaved === true) {
              savedIds.add(program.id);
            }
          });
          setSavedPrograms(prev => {
            const newSet = new Set(prev);
            savedIds.forEach(id => newSet.add(id));
            return newSet;
          });
        }
        else {
          setPrograms([]);
          setSavedPrograms(new Set());
        }
        if (ret.count) {
          setCount(ret.count);
        }
        else{
          setCount(0);
        }
      }
      setLoading(false);
    }
    catch(ex){
      setLoading(false);
      console.log('Error:',ex);
    }    
  }

  const handlePageChange = async (page: number) => {
    setCurrentPage(page);
    await getData(undefined, page, categories);
    // Scroll to top of results
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const loadCategories = async(): Promise<any[]> => {
    try {
      setLoading(true)
      let list:any[] = await wpGeodirServices.getCategories();
      let arr:any[] = (list || []).map((item)=> {
        return {
          value: item.name != null ? String(item.name) : '',
          name: item.name,
          programCount: item.programCount,
        };
      })
      const totalProgramCount = (list || []).reduce((sum: number, item: any) => {
        const n = item?.programCount;
        return sum + (typeof n === 'number' && !Number.isNaN(n) ? n : 0);
      }, 0);
      arr.splice(0, 0, { value: '', name: 'All Categories', programCount: totalProgramCount });
      setCategoryOptions(arr);
      setCategories(list);
      setLoading(false);
      return list || [];
    }
    catch(ex){
      setLoading(false);
      return [];
    }
  };

  const skipLocationSearchEffect = useRef(true);

  useEffect(() => {
    const loadData = async() => {
      initSearch();
      const list = await loadCategories();
      const mergedValues = buildSearchValuesFromQuery(location.search, list);
      formik.resetForm({ values: mergedValues });
      await getData(mergedValues, currentPage, list);
    }
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    loadData();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (skipLocationSearchEffect.current) {
      skipLocationSearchEffect.current = false;
      return;
    }
    initSearch();
    setCurrentPage(1);
    const searchValues = buildSearchValuesFromQuery(location.search, categories);
    formik.resetForm({ values: searchValues });
    getData(searchValues, 1, categories);
  }, [location.search]);

  const handleSearch = async (updatedValues?: any) => {
    query.set('geodir_search', '1');

    // Use updatedValues if provided, otherwise fall back to formik.values
    const values = updatedValues || formik.values;

    if (values.category) {
      query.set('category', values.category);
    } else {
      query.delete('category');
    }

    if (values.title) {
      query.set('name', values.title);
    } else {
      query.delete('name');
    }

    if (values.offer_in) {
      query.set('offer_in', values.offer_in);
    } else {
      query.delete('offer_in');
    }

    if (values.grades) {
      query.set('grades', values.grades);
    } else {
      query.delete('grades');
    }

    if (values.format) {
      query.set('format', values.format);
    } else {
      query.delete('format');
    }

    if (values.location) {
      query.set('location', values.location);
    } else {
      query.delete('location');
    }

    if (values.delivery) {
      query.set('delivery', values.delivery);
    } else {
      query.delete('delivery');
    }

    if (values.cost_range) {
      query.set('cost_range', values.cost_range);
    } else {
      query.delete('cost_range');
    }

    if (values.duration) {
      query.set('duration', values.duration);
    } else {
      query.delete('duration');
    }

    if (values.min_cost !== undefined && values.min_cost !== '') {
      query.set('min_cost', values.min_cost);
    } else {
      query.delete('min_cost');
    }
    if (values.max_cost !== undefined && values.max_cost !== '') {
      query.set('max_cost', values.max_cost);
    } else {
      query.delete('max_cost');
    }
    if (values.application_deadline) {
      query.set('application_deadline', values.application_deadline);
    } else {
      query.delete('application_deadline');
    }
  
    // Use the current pathname to maintain the page mode
    history.push({
      pathname: location.pathname,
      search: query.toString(),
    });

    // Reset to page 1 when searching
    setCurrentPage(1);
    // Update getData to use the updated values
    await getData(values, 1, categories);
  }

  const handleEnterKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      formik.handleSubmit();
    }
  };

  const showOptions = () => {
    var bShow = false;
    if (search_offer_in || search_grades || search_format ) {
      bShow = true;
    }
    if (formik.values.offer_in || formik.values.grades || formik.values.format) {
      bShow = true;
    }
    var ele:any = document.getElementById("dv_options");
    if (ele) {
      if (bShow) {
        ele.style.display = 'block';
      }
      else {
        ele.style.display = 'none';
      }
    }
  };


  const clearFilters = async () => {
    const clearedValues = {
      title: '',
      category: '',
      offer_in: '',
      grades: '',
      format: '',
      location: '',
      delivery: '',
      cost_range: '',
      duration: '',
      min_cost: '0',
      max_cost: '10000',
      application_deadline: '',
    };
    formik.resetForm({ values: clearedValues });
    setCurrentPage(1);
    await handleSearch(clearedValues);
  };

  // Calculate pagination values
  const totalPages = Math.ceil(count / limit);
  const getPaginationPages = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5; // Maximum visible page numbers
    
    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage <= 3) {
        // Near the start
        for (let i = 2; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };
  
  return (
    <>
      <ProgramDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        item={selectedProgram}
        isTrial={isTrial}
        isSaved={selectedProgram ? (selectedProgram.isSaved === true || savedPrograms.has(selectedProgram.id)) : false}
        isSaving={selectedProgram ? savingPrograms.has(selectedProgram.id) : false}
        onSaveToggle={toggleSaveProgram}
        disableSave={isTrial && savedPrograms.size >= 6 && selectedProgram && !(selectedProgram.isSaved === true || savedPrograms.has(selectedProgram.id))}
      />
      <Container
        style={{
          width: '100%',
          maxWidth: '100%',
          padding: isMobile ? '.5rem' : '0.5rem',
          margin: '0 auto',
        }}
      >
        <div style={{ width: '100%', maxWidth: '90rem', margin: '0 auto' }}>
          {/* Header: Browse Programs + Go to Program Manager */}
          <div className="row normal pt-2 pb-3">
            <div className="col-12 d-flex flex-wrap align-items-end justify-content-between gap-3">
              <div>
                <h1 className="mb-1" style={{ fontSize: '28px', fontWeight: 700, color: '#1a1a1a' }}>
                  Browse Programs
                </h1>
                <p className="mb-0" style={{ fontSize: '15px', color: '#666' }}>
                  Search, save, and manage your programs
                </p>
              </div>
              {!isTrial && (
                <button
                  type="button"
                  onClick={() => history.push(appRoutes.summerProgramsManager.path)}
                  className="btn d-flex align-items-center gap-2"
                  style={{
                    backgroundColor: '#2563eb',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    fontSize: '15px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <FolderIcon style={{ fontSize: '20px' }} />
                  <span>Go to Program Manager</span>
                </button>
              )}
            </div>
          </div>

          {/* Filter Card */}
          <div
            className="row normal pb-4"
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid #e5e7eb',
              padding: '32px 28px',
            }}
          >
            <div className="col-12">
              {/* Keyword Search */}
              <div style={{ marginBottom: '24px' }}>
                <label className="label-14px d-block mb-2 mt-2" style={{ marginBottom: '10px' }}>
                  Keyword Search
                </label>
                <TextField
                  className="text-field w-100"
                  name="title"
                  variant="outlined"
                  onChange={(e: any) => formik.handleChange(e)}
                  onKeyDown={handleEnterKeyPress}
                  placeholder="Search programs..."
                  value={formik.values.title}
                  fullWidth
                  size="small"
                  InputProps={{
                    style: { height: '44px', borderRadius: '8px', backgroundColor: '#fff' },
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: '#9ca3af' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgb(209, 213, 219)',
                      borderRadius: '8px',
                    },
                  }}
                  disabled={loading}
                />
              </div>

              {/* Row 1: Primary Category, Program Delivery, Duration */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginBottom: '24px' }}>
                <div style={{ flex: '1 1 0', minWidth: '180px' }}>
                  <SelectInput
                    form={formik}
                    label="Primary Category"
                    name="category"
                    option={categoryOptions}
                    renderValue={(selected: any) => selectedCategoryDisplay(selected, categoryOptions)}
                    renderOption={(opt: any) => renderCategoryOptionWithCount(opt)}
                    values={formik.values.category}
                    onChange={(e: any) => formik.setFieldValue('category', e?.target?.value ?? e)}
                    placeholder="All Categories"
                    disabled={loading}
                  />
                </div>
                <div style={{ flex: '1 1 0', minWidth: '180px' }}>
                  <SelectInput
                    form={formik}
                    label="Program Delivery"
                    name="delivery"
                    option={[
                      { value: '', name: 'All Delivery Types' },
                      { value: 'in_person', name: 'In-Person' },
                      { value: 'online', name: 'Online' },
                      { value: 'hybrid', name: 'Hybrid' },
                    ]}
                    values={formik.values.delivery}
                    onChange={(e: any) => formik.setFieldValue('delivery', e?.target?.value ?? e)}
                    placeholder="All Delivery Types"
                    disabled={loading}
                    renderValue={(selected: any) => {
                      const opts = [
                        { value: '', name: 'All Delivery Types' },
                        { value: 'in_person', name: 'In-Person' },
                        { value: 'online', name: 'Online' },
                        { value: 'hybrid', name: 'Hybrid' },
                      ];
                      const v = selected?.value ?? selected ?? '';
                      const o = opts.find((opt: any) => opt.value === v);
                      return o ? o.name : v;
                    }}
                  />
                </div>
                <div style={{ flex: '1 1 0', minWidth: '180px' }}>
                  <SelectInput
                    form={formik}
                    label="Duration"
                    name="duration"
                    option={[
                      { value: '', name: 'Any Duration' },
                      { value: '1-4', name: '1-4 weeks' },
                      { value: '4-12', name: '1-3 months' },
                      { value: '12-24', name: '3-6 months' },
                      { value: '24', name: '6+ months' },
                    ]}
                    values={formik.values.duration ?? ''}
                    onChange={(e: any) => formik.setFieldValue('duration', e?.target?.value ?? e)}
                    placeholder="Any Duration"
                    disabled={loading}
                    renderValue={(selected: any) => {
                      const v = selected?.value ?? selected ?? '';
                      const opts = [
                        { value: '', name: 'Any Duration' },
                        { value: '1-4', name: '1-4 weeks' },
                        { value: '4-12', name: '1-3 months' },
                        { value: '12-24', name: '3-6 months' },
                        { value: '24', name: '6+ months' },
                      ];
                      const o = opts.find((opt: any) => opt.value === v);
                      return o ? o.name : 'Any Duration';
                    }}
                  />
                </div>
              </div>

              {/* Row 2: Minimum Cost, Maximum Cost, Application Deadline */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginBottom: '24px' }}>
                <div style={{ flex: '1 1 0', minWidth: '180px' }}>
                  <label className="label-14px d-block mb-2" style={{ marginBottom: '10px' }}>
                    Minimum Cost
                  </label>
                  <TextField
                    name="min_cost"
                    type="text"
                    value={formik.values.min_cost}
                    onChange={(e: any) => formik.handleChange(e)}
                    placeholder="0"
                    size="small"
                    disabled={loading}
                    InputProps={{
                      style: { height: '44px', borderRadius: '8px' },
                      startAdornment: (
                        <InputAdornment position="start" style={{ color: '#6b7280' }}>$</InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgb(209, 213, 219)', borderRadius: '8px' },
                    }}
                    fullWidth
                  />
                </div>
                <div style={{ flex: '1 1 0', minWidth: '180px' }}>
                  <label className="label-14px d-block mb-2" style={{ marginBottom: '10px' }}>
                    Maximum Cost
                  </label>
                  <TextField
                    name="max_cost"
                    type="text"
                    value={formik.values.max_cost}
                    onChange={(e: any) => formik.handleChange(e)}
                    placeholder="10,000"
                    size="small"
                    disabled={loading}
                    InputProps={{
                      style: { height: '44px', borderRadius: '8px' },
                      startAdornment: (
                        <InputAdornment position="start" style={{ color: '#6b7280' }}>$</InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgb(209, 213, 219)', borderRadius: '8px' },
                    }}
                    fullWidth
                  />
                </div>
                <div style={{ flex: '1 1 0', minWidth: '220px' }}>
                  <label className="label-14px d-block mb-2" style={{ marginBottom: '10px' }}>
                    Application Deadline
                  </label>
                  <TextField
                    name="application_deadline"
                    type="date"
                    value={formik.values.application_deadline || ''}
                    onChange={(e: any) => formik.setFieldValue('application_deadline', e.target.value)}
                    placeholder="mm/dd/yyyy"
                    size="small"
                    disabled={loading}
                    InputProps={{
                      style: { height: '44px', borderRadius: '8px', minWidth: '150px' },
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarMonthIcon style={{ color: '#9ca3af', fontSize: '20px' }} />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ max: '9999-12-31' }}
                    sx={{
                      '& .MuiOutlinedInput-root': { minWidth: '150px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgb(209, 213, 219)', borderRadius: '8px' },
                    }}
                    fullWidth
                  />
                </div>
              </div>

              {/* Reset All Filters + Apply Filters */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 pt-4 border-top" style={{ borderColor: '#e5e7eb', paddingTop: '24px', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={clearFilters}
                  disabled={loading}
                  className="btn d-flex align-items-center gap-2 p-0 border-0 bg-transparent"
                  style={{
                    color: '#4b5563',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  <RestartAltIcon style={{ fontSize: '18px' }} />
                  <span>Reset All Filters</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleSearch(formik.values)}
                  disabled={loading}
                  className="btn d-flex align-items-center"
                  style={{
                    backgroundColor: '#2563eb',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    fontSize: '15px',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>

          {/* Results Summary */}
          {!loading && programs.length > 0 && (
            <div className="row normal py-3">
              <div className="col-md-12 d-flex align-items-center justify-content-between">
                <span style={{fontSize: '16px', color: '#333'}}>
                  Showing {(currentPage - 1) * limit + 1}-{Math.min(currentPage * limit, count)} of {count} programs
                </span>
                {isTrial && savedPrograms.size >= 6 && (
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>
                    Saved: {savedPrograms.size}/6 programs (upgrade to save more)
                  </span>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <LoadingContent />
          ) : (
            <div>
              {/* Only show categories when there are no search results */}
              {/* {showCategories && (!programs || programs.length === 0) && (
                <>
                  <div className="row normal py-4 mt-5">
                    <div className="col">
                      <h3 className="sub-title">Program Categories</h3>
                    </div>
                  </div>

                  {categories && categories.length>0 && (
                    <>
                      {renderCategories}
                    </>
                  )}                
                </>                
              )} */}
              {
                !loading && (!programs || programs.length === 0) && (
                  <div className="row normal py-4">
                    <div className="col-md-12 text-center">
                      <p style={{fontSize: '16px', color: '#666'}}>No programs found.</p>
                    </div>
                  </div>
                )
              }
              {
                !loading && programs && programs.length>0 && (
                  <>
                    <hr/>
                    {renderPrograms}
                    
                    {/* Pagination - show for all users */}
                    {totalPages > 1 && (
                      <div className="row normal py-4">
                        <div className="col-md-12">
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            gap: '8px',
                            flexWrap: 'wrap'
                          }}>
                            {/* Previous Button */}
                            <button
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1 || loading}
                              className="btn"
                              style={{
                                backgroundColor: '#f5f5f5',
                                color: '#666',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                padding: '8px 12px',
                                fontSize: '16px',
                                fontWeight: '500',
                                cursor: currentPage === 1 || loading ? 'not-allowed' : 'pointer',
                                opacity: currentPage === 1 || loading ? 0.5 : 1,
                                minWidth: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              &lt;
                            </button>

                            {/* Page Numbers */}
                            {getPaginationPages().map((page, index) => {
                              if (page === '...') {
                                return (
                                  <span
                                    key={`ellipsis-${index}`}
                                    style={{
                                      padding: '8px 12px',
                                      color: '#666',
                                      fontSize: '16px',
                                      minWidth: '40px',
                                      height: '40px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    ...
                                  </span>
                                );
                              }

                              const pageNum = page as number;
                              const isActive = pageNum === currentPage;

                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => handlePageChange(pageNum)}
                                  disabled={loading}
                                  className="btn"
                                  style={{
                                    backgroundColor: isActive ? '#2563eb' : '#f5f5f5',
                                    color: isActive ? '#fff' : '#666',
                                    border: isActive ? 'none' : '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    padding: '8px 12px',
                                    fontSize: '16px',
                                    fontWeight: '500',
                                    cursor: loading ? 'wait' : 'pointer',
                                    opacity: loading ? 0.7 : 1,
                                    minWidth: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  {pageNum}
                                </button>
                              );
                            })}

                            {/* Next Button */}
                            <button
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages || loading}
                              className="btn"
                              style={{
                                backgroundColor: '#f5f5f5',
                                color: '#666',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                padding: '8px 12px',
                                fontSize: '16px',
                                fontWeight: '500',
                                cursor: currentPage === totalPages || loading ? 'not-allowed' : 'pointer',
                                opacity: currentPage === totalPages || loading ? 0.5 : 1,
                                minWidth: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              &gt;
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  
                  {/* Upgrade CTA for Starter - show when at save limit or as general upsell */}
                  {/* {isSummerProgramsPage && isTrial && (
                    <div className="row normal py-4">
                      <div className="col-md-12">
                        <div
                          style={{
                            width: '100%',
                            borderRadius: '16px',
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
                            padding: '40px 32px',
                            textAlign: 'center',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              marginBottom: '20px',
                            }}
                          >
                            <div
                              style={{
                                width: '56px',
                                height: '56px',
                                borderRadius: '50%',
                                backgroundColor: '#eff6ff',
                                border: '2px solid #2563eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <LockIcon style={{ fontSize: '28px', color: '#2563eb' }} />
                            </div>
                          </div>

                          <h3
                            style={{
                              margin: 0,
                              marginBottom: '12px',
                              fontSize: '22px',
                              fontWeight: 700,
                              color: '#111827',
                            }}
                          >
                            {savedPrograms.size >= 6
                              ? 'Save More Programs'
                              : 'Upgrade for Unlimited Saves'}
                          </h3>

                          <p
                            style={{
                              margin: 0,
                              marginBottom: '28px',
                              fontSize: '15px',
                              color: '#6b7280',
                              lineHeight: 1.6,
                              maxWidth: '480px',
                              marginLeft: 'auto',
                              marginRight: 'auto',
                            }}
                          >
                            {savedPrograms.size >= 6
                              ? 'You can save up to 6 programs on Starter. Upgrade to Silver+ for unlimited saved programs and more features.'
                              : 'Upgrade to Silver+ for unlimited saved programs, advanced filters, and personalized recommendations.'}
                          </p>

                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '12px',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <button
                              type="button"
                              className="btn"
                              onClick={() => history.push(appRoutes.upgradeToSilverPlus.path)}
                              style={{
                                backgroundColor: '#2563eb',
                                color: '#fff',
                                border: 'none',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                fontSize: '15px',
                                fontWeight: 600,
                                minWidth: '140px',
                                cursor: 'pointer',
                              }}
                            >
                              View Plans
                            </button>
                            <button
                              type="button"
                              className="btn"
                              onClick={() => window.open('https://www.gradmap.com/pricing/', '_blank')}
                              style={{
                                backgroundColor: '#fff',
                                color: '#374151',
                                border: '1px solid #d1d5db',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                fontSize: '15px',
                                fontWeight: 500,
                                minWidth: '140px',
                                cursor: 'pointer',
                              }}
                            >
                              Learn More
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )} */}
                </>
              )}
            </div>
          )}
        </div>
      </Container>
    </>
  );
};

export default ProgramsPage;

